import { useEffect, useRef } from 'react'

let modalCounter = 0
type ModalStackEntry = {
  id: string
  onClose: () => void
}

const visibleModalStack: ModalStackEntry[] = []
let isEscapeListenerAttached = false

const shouldBlurFocusedEditableElement = (): boolean => {
  const activeElement = document.activeElement as HTMLElement | null
  if (!activeElement || activeElement === document.body) return false

  const tagName = activeElement.tagName
  const isEditableTag = tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT'

  if (!isEditableTag && !activeElement.isContentEditable) {
    return false
  }

  activeElement.blur()
  return true
}

const removeFromStack = (id: string) => {
  const index = visibleModalStack.findIndex(entry => entry.id === id)
  if (index !== -1) {
    visibleModalStack.splice(index, 1)
  }
}

const handleEscape = (event: KeyboardEvent) => {
  if (event.key !== 'Escape') return
  if (event.repeat) return

  if (shouldBlurFocusedEditableElement()) {
    event.preventDefault()
    event.stopPropagation()
    return
  }

  const topMostModal = visibleModalStack[visibleModalStack.length - 1]
  if (!topMostModal) return

  event.preventDefault()
  event.stopPropagation()
  topMostModal.onClose()
}

const ensureEscapeListener = () => {
  if (isEscapeListenerAttached) return
  document.addEventListener('keydown', handleEscape)
  isEscapeListenerAttached = true
}

const removeEscapeListenerIfIdle = () => {
  if (!isEscapeListenerAttached || visibleModalStack.length > 0) return
  document.removeEventListener('keydown', handleEscape)
  isEscapeListenerAttached = false
}

export const useModalEscape = (isVisible: boolean, onClose: () => void) => {
  const modalIdRef = useRef<string | null>(null)
  if (!modalIdRef.current) {
    modalCounter += 1
    modalIdRef.current = `modal-${modalCounter}`
  }

  const latestOnCloseRef = useRef(onClose)
  latestOnCloseRef.current = onClose

  useEffect(() => {
    const modalId = modalIdRef.current
    if (!modalId) return

    if (!isVisible) {
      removeFromStack(modalId)
      removeEscapeListenerIfIdle()
      return
    }

    ensureEscapeListener()
    removeFromStack(modalId)
    visibleModalStack.push({
      id: modalId,
      onClose: () => latestOnCloseRef.current()
    })

    return () => {
      removeFromStack(modalId)
      removeEscapeListenerIfIdle()
    }
  }, [isVisible])
}
